import { Box, Divider, Stack, theme, useColorModeValue, VStack } from '@chakra-ui/react';
import { Image } from '@common/components/Image';
import { useInboxModel } from '@common/models/InboxModel';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import support from '@common/support';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from 'src/apps/invest/config';
import { InvestorCard } from './InvestorCard';
import { PartnerLogo } from './PartnerLogo';
import { SidebarMenuItem } from './SidebarMenuItem';
import { SidebarSubmenu } from './SidebarSubmenu';
import { SidebarSubmenuItem } from './SidebarSubmenuItem';

export const Sidebar = () => {
  const inboxModel = useInboxModel();
  const { investorAccount } = useInvestorAccountModel();
  const inboxCount =
    inboxModel.alerts.length +
    inboxModel.messages.filter((m) => !m.viewed && !m.dismissed).length +
    inboxModel.newIntercomUnreadMessageCount;
  const inboxCountColor =
    inboxModel.alerts.length > 0 || inboxModel.newIntercomUnreadMessageCount > 0
      ? theme.colors.red[400]
      : theme.colors.blue[400];

  return (
    <Box as="section" bg="bg-canvas" overflowY="auto" height="full">
      <VStack
        bg="bg-surface"
        boxSizing="border-box"
        boxShadow={useColorModeValue('sm', 'sm-dark')}
        minH={'100%'}
        py={{ base: '6', sm: '8' }}
        px={{ base: '4', sm: '6' }}
        pb={'80px'}>
        <Stack justify="space-between" spacing="5" width="full">
          <Stack spacing={'5'} shouldWrapChildren>
            <Link to={ROUTES.dashboard}>
              <Image image="logo-beta" width={['100px', '166px']} cursor="pointer" />
            </Link>
            <Stack>
              {investorAccount ? <InvestorCard investorAccount={investorAccount} /> : null}
              <Divider />

              <Stack spacing="1">
                <SidebarMenuItem icon={'grid-2-2'} label={'Dashboard'} route={ROUTES.dashboard} />
                <SidebarSubmenu icon={'building'} label={'Invest'}>
                  <SidebarSubmenuItem
                    description="Own single-family rentals with income and appreciation potential."
                    image="sfr-icon"
                    label={'Home Shares'}
                    route={`${ROUTES.properties}`}
                    tag="Equity"
                  />
                  <SidebarSubmenuItem
                    description="Build income with a diversified portfolio of fully managed real estate debt and income equity."
                    image="cash-flow-icon"
                    label={'Cash Flow'}
                    route={`${ROUTES.offeringsCashflow}`}
                    tag="Debt"
                  />
                </SidebarSubmenu>
                <SidebarMenuItem icon={'map-pin'} label={'Roadmap'} route={ROUTES.roadmap} iconVariant="solid" />
              </Stack>
              <Divider />
              <Stack spacing="1">
                <SidebarMenuItem label="Activity" iconVariant="solid" icon="list-timeline" route={ROUTES.activity} />
                <SidebarMenuItem label="Personal Information" icon="id-card" route={ROUTES.accountInformation} />
                <SidebarMenuItem label="Profile" icon="scale-unbalanced" route={ROUTES.profile} />
                <SidebarMenuItem
                  label="Inbox"
                  icon="inbox"
                  route={ROUTES.inbox}
                  labelTag={
                    inboxCount > 0 && (
                      <Box
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        bgColor={inboxCountColor}
                        fontSize="11px"
                        color="white"
                        minWidth="18px"
                        height="18px"
                        px="5px"
                        textOverflow={'ellipsis'}
                        overflow={'hidden'}
                        borderRadius={'20px'}>
                        {inboxCount > 99 ? '99+' : inboxCount}
                      </Box>
                    )
                  }
                />
                <SidebarMenuItem label="Auto Invest Schedule" icon="calendar" route={ROUTES.autoInvestSchedule} />
                <SidebarMenuItem label="Bank Accounts" icon="building" route={ROUTES.bankAccounts} />
                <SidebarMenuItem label="Documents" icon="file-invoice" route={ROUTES.documents} />
                <SidebarMenuItem label="Help & Support" icon="comment" onClick={support.show} />
                {investorAccount?.partner && (
                  <>
                    <Divider />
                    <PartnerLogo />
                  </>
                )}
                <Divider />
                <SidebarMenuItem label="Sign Out" icon="right-from-bracket" route={ROUTES.signout} />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </VStack>
    </Box>
  );
};
